import React, { useState } from 'react';
import { Stack, TextField, PrimaryButton, IconButton, Text, Image, MessageBar, MessageBarType, TooltipHost } from '@fluentui/react';
import { URLState, LinkData } from './URL.state';
import { URLViewStyles } from './URL.styles';
import { renderFavicon } from './URL.utils';
import IvicosStrings from 'kits/language/stringKit';

type URLViewProps = URLState;

export const URLView: React.FC<URLViewProps> = ({ links, isAdding, addLink, removeLink, reorderLinks }) => {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [error, setError] = useState<string>('');
    const [manualUrl, setManualUrl] = useState<string>('');
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [failedFavicons, setFailedFavicons] = useState<Set<string>>(new Set());

    const handleAddLink = async () => {
        const urlToAdd = manualUrl.trim();
        if (!urlToAdd) {
            setError(IvicosStrings.personalMeetingCornerStrings.url.pleaseEnterUrl);
            return;
        }

        if (links.length >= 10) {
            setError(IvicosStrings.personalMeetingCornerStrings.url.maxLinksAllowed);
            return;
        }

        setError('');
        await addLink(urlToAdd);
        setManualUrl('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAddLink();
        }
    };

    const handleUrlDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };

    const handleUrlDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleUrlDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const droppedUrl = e.dataTransfer.getData('text/plain');
        if (droppedUrl && isValidUrl(droppedUrl)) {
            addLink(droppedUrl);
        } else {
            setError(IvicosStrings.personalMeetingCornerStrings.url.pleaseDropValidUrl);
        }
    };

    const isValidUrl = (string: string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleFaviconError = (faviconUrl: string) => {
        setFailedFavicons((prev) => new Set(prev).add(faviconUrl));
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleReorderDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleReorderDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        if (dragIndex !== null && dragIndex !== dropIndex) {
            reorderLinks(dragIndex, dropIndex);
        }
        setDragIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
    };

    const renderLinkCard = (link: LinkData, index: number) => (
        <Stack
            key={link.id}
            className={URLViewStyles.linkCard}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleReorderDragOver}
            onDrop={(e) => handleReorderDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{
                opacity: dragIndex === index ? 0.5 : 1,
                cursor: 'grab',
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)'
            }}
        >
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                    {renderFavicon({
                        favicon: link.favicon,
                        failedFavicons,
                        onFaviconError: handleFaviconError,
                        size: 'medium'
                    })}
                    <Stack>
                        <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                            {link.title}
                        </Text>
                        {link.description && (
                            <Text variant="small" styles={{ root: { color: '#666' } }}>
                                {link.description}
                            </Text>
                        )}
                        <Text variant="small" styles={{ root: { color: '#999' } }}>
                            {link.siteName || new URL(link.url).hostname}
                        </Text>
                    </Stack>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <TooltipHost content={IvicosStrings.personalMeetingCornerStrings.tooltips.openLink}>
                        <IconButton
                            iconProps={{ iconName: 'OpenInNewWindow' }}
                            onClick={() => window.open(link.url, '_blank')}
                            title={IvicosStrings.personalMeetingCornerStrings.url.openLink}
                        />
                    </TooltipHost>
                    <TooltipHost content={IvicosStrings.personalMeetingCornerStrings.tooltips.removeLink}>
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => removeLink(link.id)}
                            title={IvicosStrings.personalMeetingCornerStrings.url.removeLink}
                        />
                    </TooltipHost>
                </Stack>
            </Stack>
        </Stack>
    );

    const renderModalView = () => (
        <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="large">
                    {links.length === 1
                        ? IvicosStrings.personalMeetingCornerStrings.url.recommendedLink
                        : IvicosStrings.personalMeetingCornerStrings.url.recommendedLinksReorder}
                </Text>
            </Stack>

            <Stack tokens={{ childrenGap: 8 }} style={{ maxHeight: 200, overflowY: 'auto', padding: '10px 20px' }}>
                {links.map((link, index) => renderLinkCard(link, index))}
            </Stack>
        </Stack>
    );

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {/* Add URL Section */}
            <Stack tokens={{ childrenGap: 16 }}>
                {/* Drag & Drop Area */}
                <Stack
                    tokens={{ childrenGap: 8 }}
                    style={{
                        border: `2px dashed ${isDragOver ? '#0078d4' : '#e0e0e0'}`,
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                        transition: 'all 0.2s ease'
                    }}
                    onDragOver={handleUrlDragOver}
                    onDragLeave={handleUrlDragLeave}
                    onDrop={handleUrlDrop}
                >
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
                        <IconButton
                            iconProps={{ iconName: 'Globe' }}
                            styles={{
                                root: {
                                    width: 32,
                                    height: 32,
                                    backgroundColor: 'transparent',
                                    pointerEvents: 'none'
                                }
                            }}
                        />
                        <Text variant="medium" styles={{ root: { textAlign: 'center' } }}>
                            {IvicosStrings.personalMeetingCornerStrings.url.dragDropUrls}
                        </Text>
                        <Text variant="small" styles={{ root: { color: '#666', textAlign: 'center' } }}>
                            {IvicosStrings.personalMeetingCornerStrings.url.enterUrlsManually}
                        </Text>
                    </Stack>
                </Stack>

                {/* Manual URL Entry */}
                <Stack tokens={{ childrenGap: 8 }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <TextField
                            placeholder={IvicosStrings.personalMeetingCornerStrings.url.enterUrlPlaceholder}
                            value={manualUrl}
                            onChange={(_, newValue) => setManualUrl(newValue || '')}
                            onKeyPress={handleKeyPress}
                            styles={{ root: { flex: 1 } }}
                        />
                        <PrimaryButton
                            text={IvicosStrings.personalMeetingCornerStrings.url.add}
                            onClick={handleAddLink}
                            disabled={isAdding || !manualUrl.trim() || links.length >= 10}
                            iconProps={isAdding ? { iconName: 'Spinner' } : { iconName: 'Add' }}
                        />
                    </Stack>
                </Stack>

                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

                <Text variant="small" styles={{ root: { color: '#666' } }}>
                    {links.length}/10 {IvicosStrings.personalMeetingCornerStrings.url.links}
                </Text>
            </Stack>

            {/* Links Display */}
            {links.length > 0 && <Stack tokens={{ childrenGap: 16 }}>{renderModalView()}</Stack>}

            {/* Empty State */}
            {links.length === 0 && (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" styles={{ root: { color: '#666' } }}>
                        {IvicosStrings.personalMeetingCornerStrings.url.noLinksAdded}
                    </Text>
                    <Text variant="small" styles={{ root: { color: '#999' } }}>
                        {IvicosStrings.personalMeetingCornerStrings.url.addFirstLink}
                    </Text>
                </Stack>
            )}
        </Stack>
    );
};
